import React from 'react';
import { useSinglePriceFlash, flashClass, flashBgClass, FlashDirection } from '../../hooks/usePriceFlash';

interface LivePriceProps {
  price:       number;
  prefix?:     string;       // e.g. '$'
  decimals?:   number;       // decimal places
  className?:  string;       // extra classes on the outer span
  showArrow?:  boolean;      // show ▲ ▼ arrow next to price
  chipMode?:   boolean;      // wrap in a rounded chip with bg flash
  baseColor?:  string;       // default text color when not flashing
}

/**
 * LivePrice
 *
 * Drop-in component for any price that should flash green/red
 * when it changes (driven by the Binance WebSocket via Redux).
 *
 * Examples:
 *   <LivePrice price={prices['BTC']} prefix="$" />
 *   <LivePrice price={prices['ETH']} prefix="$" chipMode showArrow />
 *   <LivePrice price={prices['SOL']} className="text-xl font-bold" />
 */
const LivePrice: React.FC<LivePriceProps> = ({
  price,
  prefix    = '$',
  decimals,
  className = '',
  showArrow = false,
  chipMode  = false,
  baseColor = 'text-white',
}) => {
  const flash = useSinglePriceFlash(price);

  // Auto decimals: crypto under $1 gets 4dp, under $100 gets 2dp, else locale default
  const dp = decimals ?? (price < 1 ? 4 : price < 100 ? 2 : undefined);
  const formatted = price > 0
    ? prefix + (dp !== undefined ? price.toFixed(dp) : price.toLocaleString())
    : '—';

  const arrow = flash === 'up' ? ' ▲' : flash === 'down' ? ' ▼' : '';

  if (chipMode) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg font-mono text-xs font-bold ${flashBgClass(flash)} ${flashClass(flash, baseColor)} ${className}`}>
        {formatted}{showArrow && arrow}
      </span>
    );
  }

  return (
    <span className={`font-mono ${flashClass(flash, baseColor)} ${className}`}>
      {formatted}{showArrow && <span className="text-[10px] ml-0.5">{arrow}</span>}
    </span>
  );
};

export default LivePrice;
